import { useParams, Link } from "react-router-dom";
import { ArrowLeft, ExternalLink, ShieldCheck, CheckCircle2 } from "lucide-react";
import SEO from "../components/SEO";
import { PRODUCTS, MANUFACTURERS } from "../data/products";

export default function ProductDetail() {
  const { productId } = useParams();
  const product = PRODUCTS.find((p) => p.id === productId);

  if (!product) {
    return (
      <div className="hc-container py-32 text-center">
        <h1 className="font-serif text-3xl font-semibold">Product not found</h1>
        <Link to="/technology" className="btn-primary mt-6 inline-flex">Back to Technology</Link>
      </div>
    );
  }

  const m = MANUFACTURERS.find((x) => x.id === product.manufacturer);

  return (
    <>
      <SEO
        title={product.name}
        description={`${product.short} ${product.description.slice(0, 150)}`}
      />
      <div className="hc-container py-10">
        <Link
          to="/technology"
          data-testid="back-to-technology"
          className="inline-flex items-center gap-1.5 text-[#0369A1] font-medium hover:text-[#0284C7] text-sm"
        >
          <ArrowLeft size={16} /> Back to Technology
        </Link>
      </div>

      <section className="hc-container grid lg:grid-cols-2 gap-12 pb-16">
        <div>
          <div className="bg-gradient-to-br from-slate-50 to-white border border-slate-200 rounded-3xl p-8 aspect-square flex items-center justify-center">
            <img src={product.image} alt={product.name} className="max-h-full max-w-full object-contain" />
          </div>
          {product.gallery.length > 0 && (
            <div className="mt-4 grid grid-cols-5 gap-2">
              {product.gallery.slice(0, 5).map((g, i) => (
                <div key={i} className="aspect-square bg-white border border-slate-200 rounded-lg p-1.5">
                  <img src={g} alt={`${product.name} variant ${i + 1}`} className="w-full h-full object-contain" loading="lazy" />
                </div>
              ))}
            </div>
          )}
        </div>

        <div>
          <div className="text-[#0F766E] uppercase tracking-wider text-[12px] font-bold mb-2">
            {m?.name} · {product.category}
          </div>
          <h1 className="font-serif text-3xl md:text-4xl font-semibold leading-tight text-slate-900">
            {product.name}
          </h1>
          <p className="mt-4 text-slate-700 text-[1.1rem] leading-relaxed">{product.short}</p>

          <div className="mt-5 flex flex-wrap gap-2">
            {product.audience.map((a) => (
              <span key={a} className="chip bg-[#F0F9FF] text-[#0369A1]">
                {a === "child" ? "For Children" : "For Adults"}
              </span>
            ))}
            <span className="chip bg-emerald-50 text-emerald-700">
              <ShieldCheck size={12} /> Genuine source
            </span>
          </div>

          <p className="mt-6 text-slate-700 leading-relaxed">{product.description}</p>

          <h2 className="mt-8 font-serif text-xl font-semibold text-slate-900">Key features</h2>
          <ul className="mt-3 space-y-2">
            {product.keyFeatures.map((f, i) => (
              <li key={i} className="flex gap-2.5 text-slate-700 text-[15.5px]">
                <CheckCircle2 size={18} className="text-[#0F766E] shrink-0 mt-0.5" />
                {f}
              </li>
            ))}
          </ul>

          <div className="mt-8 bg-slate-50 border border-slate-200 rounded-xl p-5">
            <div className="text-[12px] uppercase font-bold text-slate-500 tracking-wider">Source</div>
            <a
              href={product.source}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="product-source-link"
              className="mt-1 inline-flex items-center gap-1.5 text-[#0369A1] font-medium hover:text-[#0284C7]"
            >
              {product.sourceLabel} <ExternalLink size={15} />
            </a>
          </div>

          <div className="mt-8 flex flex-wrap gap-3">
            <Link to="/cochlear-implant-eligibility" data-testid="product-eligibility-cta" className="btn-primary">
              Check Eligibility for CI
            </Link>
            <Link to="/register" data-testid="product-counsellor-cta" className="btn-secondary">
              Speak to HearClear Counsellor
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
